import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CashFlowChart = ({ data = [] }) => {
  const [chartView, setChartView] = useState('net'); // net, detailed, trends

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 card-shadow">
          <p className="text-sm font-medium text-foreground mb-2">{`${label} 2025`}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm flex items-center justify-between">
              <span style={{ color: entry?.color }}>
                {entry?.dataKey === 'inflow' ? 'Cash Inflow' : 
                 entry?.dataKey === 'outflow' ? 'Cash Outflow' : 'Net Cash Flow'}:
              </span>
              <span className="ml-2 font-medium" style={{ color: entry?.color }}>
                {formatCurrency(entry?.value)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getBars = () => {
    switch (chartView) {
      case 'detailed':
        return (
          <>
            <Bar 
              dataKey="inflow" 
              fill="#10B981" 
              name="Cash Inflow"
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="outflow" 
              fill="#EF4444" 
              name="Cash Outflow"
              radius={[2, 2, 0, 0]}
            />
          </>
        );
      case 'trends':
        return (
          <Bar 
            dataKey="net" 
            fill={(entry) => entry?.net > 0 ? "#10B981" : "#EF4444"}
            name="Net Cash Flow"
            radius={[4, 4, 0, 0]}
          />
        );
      default:
        return (
          <Bar 
            dataKey="net" 
            name="Net Cash Flow"
            radius={[4, 4, 0, 0]}
            fill={(entry, index) => {
              const value = data?.[index]?.net || 0;
              return value > 0 ? "#10B981" : "#EF4444";
            }}
          >
            {data?.map((entry, index) => (
              <Bar key={`cell-${index}`} fill={entry?.net > 0 ? "#10B981" : "#EF4444"} />
            ))}
          </Bar>
        );
    }
  };

  const getTotalCashFlow = () => {
    return data?.reduce((sum, item) => sum + item?.net, 0);
  };

  const getAverageCashFlow = () => {
    return data?.length > 0 ? getTotalCashFlow() / data?.length : 0;
  };

  const getPositiveMonths = () => {
    return data?.filter(item => item?.net > 0)?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Cash Flow Analysis</h3>
            <p className="text-sm text-muted-foreground">Monthly cash inflow and outflow tracking</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setChartView('net')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartView === 'net' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Net
            </button>
            <button
              onClick={() => setChartView('detailed')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartView === 'detailed' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Detailed
            </button>
            <button
              onClick={() => setChartView('trends')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                chartView === 'trends' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Trends
            </button>
          </div>
          <Button variant="ghost" size="sm" iconName="FileText">
            Details
          </Button>
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-muted-foreground"
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-muted-foreground"
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
            />
            {getBars()}
            <ReferenceLine 
              y={0} 
              stroke="#6B7280" 
              strokeDasharray="2 2"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Total Cash Flow</div>
          <div className={`text-lg font-semibold ${getTotalCashFlow() > 0 ? 'text-success' : 'text-error'}`}>
            {formatCurrency(getTotalCashFlow())}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Average Monthly</div>
          <div className={`text-lg font-semibold ${getAverageCashFlow() > 0 ? 'text-success' : 'text-error'}`}>
            {formatCurrency(getAverageCashFlow())}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Positive Months</div>
          <div className="text-lg font-semibold text-primary">
            {getPositiveMonths()}/{data?.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashFlowChart;